import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import loader from "../../../../methods/loader";
import ChatbotApiClient from "../../../../methods/api/chatbotapiClient";
import { useSelector } from "react-redux";
import ApiClient from "../../../../methods/api/apiClient";
import Checkbox from "../../../../components/checkbox/checkbox";

const WebsiteCrawler = ({ chatbotData }) => {
  const user = useSelector((state) => state.user);
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawledUrls, setCrawledUrls] = useState([]);
  const [trainedUrls, setTrainedUrls] = useState([]);
  const [isTraining, setIsTraining] = useState(false);

  const isValidUrl = (val) => {
    try {
      const u = new URL(val);
      return u.protocol === "https:" || u.protocol === "http:";
    } catch {
      return false;
    }
  };

  const fetchTrainedUrls = async () => {
    await ApiClient.get(`sessions/listing?urlSession=true`).then((res) => {
      setTrainedUrls(res?.data || []);
    });
  };

  useEffect(() => {
      fetchTrainedUrls();
  }, []);

  const handleFetchLinks = async () => {
    setUrlError("");
    if (!url.trim()) {
      setUrlError("Please enter a URL.");
      return;
    }
    if (!isValidUrl(url.trim())) {
      setUrlError("Please enter a valid URL (e.g. https://example.com).");
      return;
    }
    setIsCrawling(true);
    try {
      const res = await ChatbotApiClient.post(`scrap/content`, {
        urldata: url.trim(),
        depth: 3,
        user_id: user?.id||user?._id,
      });
      const links = Array.isArray(res) ? res : res?.links_data || res?.data || [];
      setCrawledUrls(links.map((item) => ({ ...item, checked: false })));
    } catch {
      setUrlError("Failed to fetch links. Please try again.");
    } finally {
      setIsCrawling(false);
    }
  };

  const toggleCheck = (index) => {
    setCrawledUrls((prev) =>
      prev.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item))
    );
  };

  const toggleSelectAll = () => {
    const allChecked = crawledUrls.every((u) => u.checked);
    setCrawledUrls((prev) => prev.map((item) => ({ ...item, checked: !allChecked })));
  };

  const selectedUrls = crawledUrls.filter((u) => u.checked);
  const allChecked = crawledUrls.length > 0 && crawledUrls.every((u) => u.checked);

  const handleTrain = async () => {
    if (selectedUrls.length === 0) return;
    setIsTraining(true);
    loader(true);
    try {
      await ChatbotApiClient.post(`scrap/train/ai`, {
        urls: selectedUrls
      });
      swal({
        text: "Chatbot training started! It may take a few seconds.",
        icon: "success",
        className: "smallModal",
      });
      setCrawledUrls([]);
      setUrl("");
      await fetchTrainedUrls();
    } catch {
      swal({ text: "Something went wrong. Please try again.", icon: "error", className: "smallModal" });
    } finally {
      setIsTraining(false);
      loader(false);
    }
  };

  const handleDeleteTrained = async (id) => {
    const confirm = await swal({ text: `Delete selected url?`, icon: "warning", buttons: true, dangerMode: true });
        if (!confirm) return;
    loader(true);
    await ApiClient.delete(`sessions/delete?id=${id}`)
      .then(async res => {
        await fetchTrainedUrls();
        swal({ text: 'Url deleted successfully.', icon: 'success', className: 'smallModal' });
        loader(false);
      })
      .catch(error => { })
    
  };

  return (
    <div className="w-full border border-t-0 rounded-b-2xl bg-white p-5 lg:p-8 space-y-8">

      {/* ── Crawl Section ── */}
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="size-10 rounded-xl bg-[#115b9d29] flex items-center justify-center flex-shrink-0 mt-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className="text-[#115B9D]">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
              <path d="M2 12h20" />
            </svg>
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <h4 className="text-md sm:text-lg text-gray-900 mb-1">Crawl Website</h4>
              <p className="text-sm text-gray-500">
                This will crawl all the links starting with the URL (not including files on the website).
              </p>
            </div>
            <div className="flex items-start gap-2 max-[480px]:flex-wrap">
              <div className="flex-1 min-w-0">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); setUrlError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleFetchLinks()}
                  placeholder="https://www.example.com/"
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-[#115B9D]/20 outline-none transition-all text-sm ${
                    urlError ? "border-red-400 focus:border-red-400" : "border-gray-300 focus:border-[#115B9D]"
                  }`}
                />
                {urlError && (
                  <p className="text-xs text-red-500 mt-1.5 ml-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>
                    </svg>
                    {urlError}
                  </p>
                )}
              </div>
              <button
                onClick={handleFetchLinks}
                disabled={isCrawling}
                type="button"
                className="px-6 py-3 bg-[#115B9D] text-white rounded-xl hover:bg-opacity-90 transition-colors whitespace-nowrap text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              >
                {isCrawling ? (
                  <>
                    <svg className="animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Crawling…
                  </>
                ) : "Fetch Links"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Fetched URL List ── */}
      {crawledUrls.length > 0 && (
        <div className="space-y-3">
          <div className="overflow-auto scrollbar border border-gray-200 rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-5">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={allChecked}
                        onChange={toggleSelectAll}
                      />
                      <span className="text-sm text-gray-700 font-normal">
                        Select All
                        <span className="text-gray-600 font-normal ml-1.5">
                          ({crawledUrls.length} links found)
                        </span>
                      </span>
                    </div>
                  </th>
                  <th className="text-right py-3 px-5 w-40">
                    {selectedUrls.length > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#115B9D17] text-[#115B9D] text-xs font-medium border border-[#115B9D]/20">
                        {selectedUrls.length} selected
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {crawledUrls.map((item, i) => (
                  <tr
                    key={i}
                    onClick={() => toggleCheck(i)}
                    className={`border-t border-gray-100 cursor-pointer transition-colors ${
                      item.checked ? "bg-[#6b25f90a]" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                        checked={item.checked}
                          onChange={() => toggleCheck(i)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm text-gray-700 truncate max-w-md">
                          {item.url}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-xs">
                        {item.char?.toLocaleString()} chars
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Train bar */}
          {selectedUrls.length > 0 && (
            <div className="flex items-center justify-between p-4 rounded-xl bg-[#6b25f90a] border border-[#115B9D]/20">
              <p className="text-sm text-[#115B9D] font-medium">
                {selectedUrls.length} URL{selectedUrls.length > 1 ? "s" : ""} ready to train
              </p>
              <button
                onClick={handleTrain}
                disabled={isTraining}
                type="button"
                className="px-6 py-2.5 bg-[#115B9D] text-white rounded-xl hover:bg-opacity-90 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              >
                {isTraining ? (
                  <>
                    <svg className="animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Training…
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                    Train Chatbot
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Already Included URLs ── */}
      <div className="space-y-4">
        <h3 className="text-md sm:text-lg text-gray-900">Already Included Links</h3>
        <div className="overflow-auto scrollbar border border-gray-200 rounded-xl">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-5">
                  <span className="text-sm text-gray-700 font-normal">URL</span>
                </th>
                <th className="text-left py-3 px-5 w-36">
                  <span className="text-sm text-gray-700 font-normal">Status</span>
                </th>
                <th className="text-left py-3 px-5 w-24">
                  <span className="text-sm text-gray-700 font-normal">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {trainedUrls.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="size-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                          className="text-gray-600">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                          <path d="M2 12h20" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600">No URLs trained yet</p>
                      <p className="text-xs text-gray-300">Crawl and train a website above to get started</p>
                    </div>
                  </td>
                </tr>
              ) : (
                trainedUrls.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100 first:border-t-0">
                    <td className="py-3 px-5">
                      <div className="flex gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          className="text-gray-600 flex-shrink-0">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                          <path d="M2 12h20" />
                        </svg>
                        <span className="text-sm text-gray-700 break-all max-w-md">
                          {item?.urlData?.map((_itm)=>_itm?.url)?.join(',')}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-5">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                        Trained
                      </span>
                    </td>
                    <td className="py-3 px-5">
                      <button
                        type="button"
                        onClick={() => handleDeleteTrained(item?.id||item?._id)}
                        className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                        title="Delete"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          <line x1="10" x2="10" y1="11" y2="17" />
                          <line x1="14" x2="14" y1="11" y2="17" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WebsiteCrawler;